import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import AdyenCheckout from '@adyen/adyen-web';
import '@adyen/adyen-web/dist/adyen.css';
import './adyen-card.css';

const AdyenAddCreditCard = ({
  clientKey,
  environment,
  cardBrands,
  isActive,
  onValidityChange,
}) => {
  const containerRef = useRef(null);
  const cardComponentRef = useRef(null);
  const checkoutRef = useRef(null);
  const onValidityChangeRef = useRef(onValidityChange);

  useEffect(() => {
    onValidityChangeRef.current = onValidityChange;
  }, [onValidityChange]);

  useEffect(() => {
    if (!isActive) {
      if (cardComponentRef.current && typeof cardComponentRef.current.unmount === 'function') {
        cardComponentRef.current.unmount();
      }
      cardComponentRef.current = null;
      checkoutRef.current = null;
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      onValidityChangeRef.current?.(false, null);
      return undefined;
    }

    let isMounted = true;

    const init = async () => {
      if (!clientKey || !containerRef.current) {
        return;
      }

      const checkout = await AdyenCheckout({
        environment: (environment || 'test').toLowerCase(),
        clientKey,
        onChange: (state) => {
          if (!isMounted) {
            return;
          }
          const paymentMethod = state.isValid ? state.data?.paymentMethod : null;
          onValidityChangeRef.current?.(!!state.isValid, paymentMethod);
        },
      });

      if (!isMounted || !containerRef.current) {
        return;
      }

      checkoutRef.current = checkout;
      const cardOptions = {
        billingAddressRequired: false,
        hasHolderName: false,
        showPayButton: false,
        placeholders: {
          cardNumber: 'Card number',
          expiryDate: 'MM/YY',
          securityCodeThreeDigits: 'CVV',
          securityCodeFourDigits: 'CVV',
        },
        styles: {
          base: {
            color: '#212529',
            fontSize: '16px',
            lineHeight: '24px',
            fontFamily: 'Segoe UI, sans-serif',
          },
          placeholder: {
            color: '#d0cfd1',
          },
        },
        ...(cardBrands?.length > 0 ? { brands: cardBrands } : {}),
      };

      cardComponentRef.current = checkout.create('card', cardOptions).mount(containerRef.current);
    };

    init();

    return () => {
      isMounted = false;
      if (cardComponentRef.current && typeof cardComponentRef.current.unmount === 'function') {
        cardComponentRef.current.unmount();
      }
      cardComponentRef.current = null;
      checkoutRef.current = null;
    };
  }, [clientKey, environment, isActive, cardBrands]);

  if (!isActive) {
    return null;
  }

  return (
    <div className="adyen-card-input">
      <div ref={containerRef} />
    </div>
  );
};

AdyenAddCreditCard.propTypes = {
  clientKey: PropTypes.string.isRequired,
  environment: PropTypes.string,
  cardBrands: PropTypes.arrayOf(PropTypes.string),
  isActive: PropTypes.bool,
  onValidityChange: PropTypes.func,
};

AdyenAddCreditCard.defaultProps = {
  isActive: false,
  onValidityChange: undefined,
};

export default AdyenAddCreditCard;
